import type { DashboardSummaryData } from '../types/dashboard.types';

/**
 * Transforma la respuesta de /dashboard/summary (MDL11.sp_FL_GetDashboardSummary)
 * en los valores que dibuja el dashboard.
 *
 * Definiciones (iguales al portal anterior, para que ambos portales muestren
 * los mismos números):
 *   - Ingresos             = monto de las facturas Procesadas (estado 0).
 *   - Promedio por factura = Ingresos / cantidad de Procesadas.
 *   - Total facturas       = todos los estados (cuadra con "Facturas Activas").
 * Las notas de crédito NO se restan (mismo criterio que el portal anterior).
 */

/** Estados eInvoiceStatus — mismas etiquetas que los filtros de "Facturas Activas". */
export const INVOICE_STATUS_LABELS: Record<number, string> = {
  0: 'Procesada',
  1: 'Error',
  2: 'Autorizado uso de la FE',
  3: 'Procesando',
  4: 'Pendiente',
  5: 'Anulada',
};

export const OTHER_STATUS_LABEL = 'Otros';

/** Tipos de documento (campo iDoc de la ficha técnica DGI). */
export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  '01': 'Factura de operación interna',
  '02': 'Factura de importación',
  '03': 'Factura de exportación',
  '04': 'Nota de crédito referente a FE',
  '05': 'Nota de débito referente a FE',
  '06': 'Nota de crédito genérica',
  '07': 'Nota de débito genérica',
  '08': 'Factura de zona franca',
  '09': 'Reembolso',
  '10': 'Factura de operación extranjera',
};

export const PROCESSED_STATUS = 0;
export const ERROR_STATUS = 1;

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const TOP_DOCUMENT_TYPES = 5;

export interface DashboardMonthlyPoint {
  year: number;
  monthNum: number;
  month: string;
  invoices: number;
  amount: number;
  processed: number;
  errors: number;
  average: number;
}

export interface DashboardSummaryView {
  /** Mismo formato que producía useDashboardStatusDistribution: { etiqueta, cantidad }. */
  statusDistribution: Array<{ name: string; value: number }>;
  totalInvoices: number;
  processedInvoices: number;
  revenue: number;
  revenueChangePct: number | null;
  averageInvoice: number;
  averageInvoiceChangePct: number | null;
  documentTypesCount: number;
  topDocumentTypes: Array<{ code: string; name: string; quantity: number }>;
  monthly: DashboardMonthlyPoint[];
  hasPrevious: boolean;
  appliedStartDate: string | null;
  appliedEndDate: string | null;
  windowFrom: string | null;
}

const toNumber = (value: number | string | null | undefined): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const toIsoDate = (value: string | null | undefined): string | null =>
  value ? String(value).slice(0, 10) : null;

const round2 = (value: number): number => Math.round(value * 100) / 100;

const changePct = (current: number, previous: number): number | null =>
  previous > 0 ? round2(((current - previous) / previous) * 100) : null;

export const EMPTY_DASHBOARD_SUMMARY: DashboardSummaryView = {
  statusDistribution: [],
  totalInvoices: 0,
  processedInvoices: 0,
  revenue: 0,
  revenueChangePct: null,
  averageInvoice: 0,
  averageInvoiceChangePct: null,
  documentTypesCount: 0,
  topDocumentTypes: [],
  monthly: [],
  hasPrevious: false,
  appliedStartDate: null,
  appliedEndDate: null,
  windowFrom: null,
};

export function buildDashboardSummary(data: DashboardSummaryData | null | undefined): DashboardSummaryView {
  if (!data) return EMPTY_DASHBOARD_SUMMARY;

  const meta = data.meta;
  const hasPrevious = meta ? meta.has_previous === true || meta.has_previous === 1 : false;

  const statusTotals = new Map<string, number>();
  const docTypeTotals = new Map<string, number>();
  let totalInvoices = 0;
  let processedInvoices = 0;
  let revenue = 0;
  let prevProcessedInvoices = 0;
  let prevRevenue = 0;

  for (const row of data.period ?? []) {
    const invoices = toNumber(row.invoices);
    const amount = toNumber(row.amount);
    const isProcessed = row.e_invoice_status === PROCESSED_STATUS;

    if (row.period === 'previous') {
      if (isProcessed) {
        prevProcessedInvoices += invoices;
        prevRevenue += amount;
      }
      continue;
    }

    totalInvoices += invoices;
    if (isProcessed) {
      processedInvoices += invoices;
      revenue += amount;
    }

    const statusLabel =
      row.e_invoice_status !== null && INVOICE_STATUS_LABELS[row.e_invoice_status]
        ? INVOICE_STATUS_LABELS[row.e_invoice_status]
        : OTHER_STATUS_LABEL;
    statusTotals.set(statusLabel, (statusTotals.get(statusLabel) ?? 0) + invoices);

    const docCode = row.document_type?.trim() || '';
    docTypeTotals.set(docCode, (docTypeTotals.get(docCode) ?? 0) + invoices);
  }

  const averageInvoice = processedInvoices > 0 ? revenue / processedInvoices : 0;
  const prevAverageInvoice = prevProcessedInvoices > 0 ? prevRevenue / prevProcessedInvoices : 0;

  const statusDistribution = Array.from(statusTotals.entries()).map(([name, value]) => ({ name, value }));

  const topDocumentTypes = Array.from(docTypeTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_DOCUMENT_TYPES)
    .map(([code, quantity]) => ({
      code,
      name: code ? (DOCUMENT_TYPE_LABELS[code] ? `${code} · ${DOCUMENT_TYPE_LABELS[code]}` : code) : 'Sin tipo',
      quantity,
    }));

  const monthlyMap = new Map<string, DashboardMonthlyPoint>();
  for (const row of data.monthly ?? []) {
    const year = toNumber(row.year);
    const monthNum = toNumber(row.month_num);
    const key = `${year}-${monthNum}`;
    const point =
      monthlyMap.get(key) ??
      { year, monthNum, month: MONTH_LABELS[monthNum - 1] ?? String(monthNum), invoices: 0, amount: 0, processed: 0, errors: 0, average: 0 };
    const invoices = toNumber(row.invoices);
    point.invoices += invoices;
    if (row.e_invoice_status === PROCESSED_STATUS) {
      point.processed += invoices;
      point.amount += toNumber(row.amount);
    }
    if (row.e_invoice_status === ERROR_STATUS) {
      point.errors += invoices;
    }
    monthlyMap.set(key, point);
  }

  const monthly = Array.from(monthlyMap.values())
    .map((p) => ({ ...p, amount: round2(p.amount), average: p.processed > 0 ? p.amount / p.processed : 0 }))
    .sort((a, b) => a.year - b.year || a.monthNum - b.monthNum);

  return {
    statusDistribution,
    totalInvoices,
    processedInvoices,
    revenue: round2(revenue),
    revenueChangePct: hasPrevious ? changePct(revenue, prevRevenue) : null,
    averageInvoice,
    averageInvoiceChangePct: hasPrevious ? changePct(averageInvoice, prevAverageInvoice) : null,
    documentTypesCount: docTypeTotals.size,
    topDocumentTypes,
    monthly,
    hasPrevious,
    appliedStartDate: toIsoDate(meta?.start_date),
    appliedEndDate: toIsoDate(meta?.end_date),
    windowFrom: toIsoDate(meta?.window_from),
  };
}

/**
 * Primer día de la ventana que acepta el SP: mes de endDate + 2 meses anteriores.
 * Se usa como `min` del selector de fecha inicio para no pedir un rango que el
 * servidor igual recortaría.
 */
export function getDashboardWindowStart(endDate: string, monthsBack: number = 2): string {
  const [y, m] = endDate.split('-').map(Number);
  if (!y || !m) return endDate;
  const start = new Date(Date.UTC(y, m - 1 - monthsBack, 1));
  return start.toISOString().slice(0, 10);
}
